import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { BountyCard, DiscoverSortBy, WorkMode } from "@/lib/types";
import type { ExchangeCategory } from "@/services/interfaces/IExchangeService";
import {
  budgetRangeFor,
  DEFAULT_FILTERS,
  type BudgetPreset,
  type DiscoverFilters,
} from "@/features/bounty/discover/discoverParams";


export type ViewMode = "grid" | "list" | "tinder" | "map";
/**
 * What the single Discover feed shows: quests and Xare offers mixed, or one
 * kind alone. Replaced the old Quests | Xare tabs.
 */
export type FeedType = "all" | "quests" | "xare";

interface DiscoverStore {
  // View & display
  feedType: FeedType;
  /** Category chip on the Xare-only feed; not persisted. */
  xareCategory: ExchangeCategory | "all";
  viewMode: ViewMode;
  sortBy: DiscoverSortBy;
  searchQuery: string;
  budgetPreset: BudgetPreset;

  // Card state (tinder)
  cards: BountyCard[];
  currentIndex: number;
  filters: DiscoverFilters;

  // Selected bounty for detail sheet
  selectedBountyId: string | null;

  // Actions
  setFeedType: (type: FeedType) => void;
  setXareCategory: (category: ExchangeCategory | "all") => void;
  setViewMode: (mode: ViewMode) => void;
  setSortBy: (sortBy: DiscoverSortBy) => void;
  setSearchQuery: (query: string) => void;
  setBudgetPreset: (preset: BudgetPreset) => void;
  setSelectedBountyId: (id: string | null) => void;
  setCards: (cards: BountyCard[]) => void;
  swipeRight: (id: string) => void;
  swipeLeft: (id: string) => void;
  advanceCard: () => void;
  toggleSkillFilter: (skill: string) => void;
  toggleCategory: (category: string) => void;
  setLocationCountry: (country: string) => void;
  setLocationCity: (city: string) => void;
  setWorkMode: (mode: WorkMode | "") => void;
  resetFilters: () => void;
}

export const useDiscoverStore = create<DiscoverStore>()(
  persist(
    (set) => ({
      feedType: "all",
      xareCategory: "all",
      viewMode: "grid",
      sortBy: "newest",
      searchQuery: "",
      budgetPreset: null,
      cards: [],
      currentIndex: 0,
      filters: { ...DEFAULT_FILTERS },
      selectedBountyId: null,

      // Swipe is quest-only, so the Xare feed falls back to the grid.
      setFeedType: (type) =>
        set((s) => ({
          feedType: type,
          viewMode: type === "xare" && s.viewMode === "tinder" ? "grid" : s.viewMode,
        })),
      setXareCategory: (category) => set({ xareCategory: category }),
      setViewMode: (mode) => set({ viewMode: mode }),
      setSortBy: (sortBy) => set({ sortBy }),
      setSearchQuery: (query) => set({ searchQuery: query }),

      setBudgetPreset: (preset) =>
        set((s) => ({
          budgetPreset: preset,
          filters: { ...s.filters, ...budgetRangeFor(preset) },
          currentIndex: 0,
        })),

      setSelectedBountyId: (id) => set({ selectedBountyId: id }),

      setCards: (cards) => set({ cards, currentIndex: 0 }),

      swipeRight: (_id) => {
        set((s) => ({ currentIndex: s.currentIndex + 1 }));
      },

      swipeLeft: (_id) => {
        set((s) => ({ currentIndex: s.currentIndex + 1 }));
      },

      advanceCard: () => set((s) => ({ currentIndex: s.currentIndex + 1 })),

      toggleSkillFilter: (skill) =>
        set((s) => ({
          filters: {
            ...s.filters,
            skills: s.filters.skills.includes(skill)
              ? s.filters.skills.filter((sk) => sk !== skill)
              : [...s.filters.skills, skill],
          },
          currentIndex: 0,
        })),

      toggleCategory: (category) =>
        set((s) => ({
          filters: {
            ...s.filters,
            categories: s.filters.categories.includes(category)
              ? s.filters.categories.filter((c) => c !== category)
              : [...s.filters.categories, category],
          },
          currentIndex: 0,
        })),

      setLocationCountry: (country) =>
        set((s) => ({
          filters: { ...s.filters, locationCountry: country },
          currentIndex: 0,
        })),

      setLocationCity: (city) =>
        set((s) => ({
          filters: { ...s.filters, locationCity: city },
          currentIndex: 0,
        })),

      setWorkMode: (mode) =>
        set((s) => ({
          filters: { ...s.filters, workMode: mode },
          currentIndex: 0,
        })),

      resetFilters: () =>
        set({
          filters: { ...DEFAULT_FILTERS },
          budgetPreset: null,
          searchQuery: "",
          currentIndex: 0,
        }),
    }),
    {
      name: "wexare-discover",
      version: 1,
      // v0 persisted `discoverTab`; a user parked on the Xare tab keeps Xare.
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Record<string, unknown>;
        if (version < 1) {
          const { discoverTab, ...rest } = state;
          return { ...rest, feedType: discoverTab === "exchange" ? "xare" : "all" };
        }
        return state;
      },
      partialize: (state) => ({
        feedType: state.feedType,
        viewMode: state.viewMode,
        sortBy: state.sortBy,
        filters: state.filters,
        budgetPreset: state.budgetPreset,
      }),
    },
  ),
);
